/**
 * GsplatMirrorClip - Shader-level clip plane for mirror reflections
 *
 * Gaussian splats bypass the standard oblique near-clip plane because PlayCanvas
 * clamps centerProj.z to prevent near/far clipping artifacts. This module provides
 * a clip plane test that runs inside modifySplatRotationScale(), killing splats
 * behind the mirror by setting scale = vec3(0.0).
 *
 * The uniform uMirrorClipPlane is toggled per camera via calculateProjection:
 * - Reflection camera's calculateProjection → sets (normal.xyz, d)
 * - Main camera's calculateProjection → clears to (0,0,0,0)
 *
 * Usage:
 * - Prepend the snippet to any gsplatModifyVS shader
 * - Call mirrorClipSplat(center, scale) in modifySplatRotationScale()
 */

/**
 * Shadow-pass variants for point (omni) lights: the engine names a light's shadow pass
 * ShadowPass_<lightType>_<shadowType> and defines SHADOWPASS_<lightType>_<shadowType>_PASS;
 * omni is light type 1, and these are the depth (PCF / PCSS) shadow types.
 */
const OMNI_PASS_DEFINE = `#if defined(SHADOWPASS_1_0_PASS) || defined(SHADOWPASS_1_4_PASS) || defined(SHADOWPASS_1_5_PASS) || defined(SHADOWPASS_1_7_PASS) || defined(SHADOWPASS_1_8_PASS) || defined(SHADOWPASS_1_9_PASS)
#define RL_OMNI_SHADOW_PASS
#endif`;

/**
 * GLSL half of the point-light caster helper, appended to the engine's gsplatOutputVS chunk so
 * it comes after the engine's matrix uniforms (GLSL needs a declaration before use; the
 * modify chunk is included first). WebGL clip depth runs -1..1: depth t is view z -B / (A + 2t - 1).
 */
export const SPLAT_CASTER_LATE_GLSL = /* glsl */ `
#if defined(RL_OMNI_SHADOW_PASS) && defined(RL_OMNI_CASTER)
void rl_omniCasterCenter(inout vec3 center) {
    mat4 mv = matrix_view * matrix_model;
    vec3 v = (mv * vec4(center, 1.0)).xyz;
    if (v.z > -1e-5) return;
    float A = matrix_projection[2][2];
    float B = matrix_projection[3][2];
    float far = B / (A + 1.0);
    float t = min(length(v) / far, 0.99999);
    float z = -B / (A + 2.0 * t - 1.0);
    rl_casterScale = z / v.z;
    // the light (camera) in model space: -R^-1 t, with R^-1 from cross products
    vec3 c0 = mv[0].xyz;
    vec3 c1 = mv[1].xyz;
    vec3 c2 = mv[2].xyz;
    vec3 tr = mv[3].xyz;
    float det = dot(c0, cross(c1, c2));
    vec3 cam = -vec3(dot(cross(c1, c2), tr), dot(cross(c2, c0), tr), dot(cross(c0, c1), tr)) / det;
    center = cam + (center - cam) * rl_casterScale;
}
#endif
`;

// ---- Mirror clip snippet (uniforms + helper function) ----
// Prepend to any gsplatModifyVS shader. Call mirrorClipSplat() in modifySplatRotationScale.

export const mirrorClipSnippetGLSL = /* glsl */ `
uniform vec4 uMirrorClipPlane;
// Which splats cast into the shadow maps (effects/shadows/shadowSystem.ts): x the largest splat
// that casts (0 = any; a scan's sky and background splats are huge and would shade everything),
// y the least opacity that casts (faint floaters would blotch the ground).
uniform vec4 uSplatCasterLimits;
${OMNI_PASS_DEFINE}
// Point-light (omni) shadow maps hold distance / range, which the engine's own shadow pass
// writes as the depth; a splat writes the depth of its projected position. rl_casterCenter
// (every composed modifySplatCenter ends with it) slides the splat along the ray from the light
// to where its projected depth reads distance / range, and mirrorClipSplat grows it by the
// same factor, so it still covers the same texels. GLSL defines the helper after the engine's
// matrices (SPLAT_CASTER_LATE_GLSL, installed with RL_OMNI_CASTER by the shadow system).
float rl_casterScale = 1.0;
#if defined(RL_OMNI_SHADOW_PASS) && defined(RL_OMNI_CASTER)
void rl_omniCasterCenter(inout vec3 center);
#endif
void rl_casterCenter(inout vec3 center) {
#if defined(RL_OMNI_SHADOW_PASS) && defined(RL_OMNI_CASTER)
    rl_omniCasterCenter(center);
#endif
}
// Shape of the splat being processed, recorded by rl_recordSplatShape, which every composed
// modifySplatRotationScale calls (next to mirrorClipSplat) before modifySplatColor runs. Shadow
// receiving (effects/shadows/splatShadowShader.ts) sizes its bias and filter from it.
// rl_splatNormal: the thinnest axis (a surface splat's normal, sign unknown); rl_splatFlat:
// 0 for a round blob, 1 for a flat disk; rl_splatSize: the largest scale.
float rl_splatSize = 0.0;
float rl_splatFlat = 0.0;
vec3 rl_splatNormal = vec3(0.0, 1.0, 0.0);

void rl_recordSplatShape(vec4 rotation, vec3 scale) {
    // rotation is (x, y, z, w); the columns of its matrix are the splat's axes
    float x = rotation.x, y = rotation.y, z = rotation.z, w = rotation.w;
    vec3 axis0 = vec3(1.0 - 2.0 * (y * y + z * z), 2.0 * (x * y + w * z), 2.0 * (x * z - w * y));
    vec3 axis1 = vec3(2.0 * (x * y - w * z), 1.0 - 2.0 * (x * x + z * z), 2.0 * (y * z + w * x));
    vec3 axis2 = vec3(2.0 * (x * z + w * y), 2.0 * (y * z - w * x), 1.0 - 2.0 * (x * x + y * y));
    float lo = min(min(scale.x, scale.y), scale.z);
    float hi = max(max(scale.x, scale.y), scale.z);
    float mid = scale.x + scale.y + scale.z - lo - hi;
    rl_splatNormal = scale.x == lo ? axis0 : (scale.y == lo ? axis1 : axis2);
    rl_splatFlat = clamp(1.0 - lo / max(mid, 1e-6), 0.0, 1.0);
    rl_splatSize = hi;
}

void mirrorClipSplat(vec3 center, inout vec3 scale) {
#ifdef SHADOW_PASS
    if (uSplatCasterLimits.x > 0.0 && max(max(scale.x, scale.y), scale.z) > uSplatCasterLimits.x) {
        scale = vec3(0.0);
    }
    scale *= rl_casterScale;
#endif
    vec3 n = uMirrorClipPlane.xyz;
    if (dot(n, n) > 0.25) {
        float dist = dot(center, n) - uMirrorClipPlane.w;
        if (dist < 0.0) {
            scale = vec3(0.0);
        }
    }
}
`;

export const mirrorClipSnippetWGSL = /* wgsl */ `
uniform uMirrorClipPlane: vec4f;
// Which splats cast into the shadow maps (see the GLSL version).
uniform uSplatCasterLimits: vec4f;
${OMNI_PASS_DEFINE}
// Point-light shadow maps hold distance / range (see the GLSL version). WebGPU's clip depth runs
// 0..1, so the view z whose depth is t is -B / (A + t).
var<private> rl_casterScale: f32 = 1.0;
fn rl_casterCenter(center: ptr<function, vec3f>) {
#ifdef RL_OMNI_SHADOW_PASS
    let mv = uniform.matrix_view * uniform.matrix_model;
    let v = (mv * vec4f(*center, 1.0)).xyz;
    if (v.z > -1e-5) { return; }
    let A = uniform.matrix_projection[2][2];
    let B = uniform.matrix_projection[3][2];
    let far = B / (A + 1.0);
    let t = min(length(v) / far, 0.99999);
    let z = -B / (A + t);
    rl_casterScale = z / v.z;
    // the light (camera) in model space: -R^-1 t, with R^-1 from cross products
    let c0 = mv[0].xyz;
    let c1 = mv[1].xyz;
    let c2 = mv[2].xyz;
    let tr = mv[3].xyz;
    let det = dot(c0, cross(c1, c2));
    let cam = -vec3f(dot(cross(c1, c2), tr), dot(cross(c2, c0), tr), dot(cross(c0, c1), tr)) / det;
    *center = cam + (*center - cam) * rl_casterScale;
#endif
}
// Shape of the splat being processed (see the GLSL version).
var<private> rl_splatSize: f32 = 0.0;
var<private> rl_splatFlat: f32 = 0.0;
var<private> rl_splatNormal: vec3f = vec3f(0.0, 1.0, 0.0);

fn rl_recordSplatShape(rotation: vec4f, scale: vec3f) {
    let x = rotation.x;
    let y = rotation.y;
    let z = rotation.z;
    let w = rotation.w;
    let axis0 = vec3f(1.0 - 2.0 * (y * y + z * z), 2.0 * (x * y + w * z), 2.0 * (x * z - w * y));
    let axis1 = vec3f(2.0 * (x * y - w * z), 1.0 - 2.0 * (x * x + z * z), 2.0 * (y * z + w * x));
    let axis2 = vec3f(2.0 * (x * z + w * y), 2.0 * (y * z - w * x), 1.0 - 2.0 * (x * x + y * y));
    let lo = min(min(scale.x, scale.y), scale.z);
    let hi = max(max(scale.x, scale.y), scale.z);
    let mid = scale.x + scale.y + scale.z - lo - hi;
    if (scale.x == lo) { rl_splatNormal = axis0; } else if (scale.y == lo) { rl_splatNormal = axis1; } else { rl_splatNormal = axis2; }
    rl_splatFlat = clamp(1.0 - lo / max(mid, 1e-6), 0.0, 1.0);
    rl_splatSize = hi;
}

fn mirrorClipSplat(center: vec3f, scale: ptr<function, vec3f>) {
#ifdef SHADOW_PASS
    let maxCaster = uniform.uSplatCasterLimits.x;
    if (maxCaster > 0.0 && max(max((*scale).x, (*scale).y), (*scale).z) > maxCaster) {
        *scale = vec3f(0.0);
    }
    *scale = *scale * rl_casterScale;
#endif
    let n = uniform.uMirrorClipPlane.xyz;
    if (dot(n, n) > 0.25) {
        let dist = dot(center, n) - uniform.uMirrorClipPlane.w;
        if (dist < 0.0) {
            *scale = vec3f(0.0);
        }
    }
}
`;

// ---- Standalone shader (used when no other effect is active) ----

export const mirrorClipShaderGLSL = mirrorClipSnippetGLSL + /* glsl */ `
void modifySplatCenter(inout vec3 center) {
    rl_casterCenter(center);
}

void modifySplatRotationScale(vec3 originalCenter, vec3 modifiedCenter, inout vec4 rotation, inout vec3 scale) {
    rl_recordSplatShape(rotation, scale);
    mirrorClipSplat(modifiedCenter, scale);
}

void modifySplatColor(vec3 center, inout vec4 color) {}
`;

export const mirrorClipShaderWGSL = mirrorClipSnippetWGSL + /* wgsl */ `
fn modifySplatCenter(center: ptr<function, vec3f>) {
    rl_casterCenter(center);
}

fn modifySplatRotationScale(originalCenter: vec3f, modifiedCenter: vec3f, rotation: ptr<function, vec4f>, scale: ptr<function, vec3f>) {
    rl_recordSplatShape(*rotation, *scale);
    mirrorClipSplat(modifiedCenter, scale);
}

fn modifySplatColor(center: vec3f, color: ptr<function, vec4f>) {}
`;
